import { useRef, useEffect, useState } from "react";
import { fmtDate, fmtDateTime } from "../lib/format";
import { RotateCcw, Save, Download } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";

export default function HandoverPanel({ job, onUpdate, token }) {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [handoverDate, setHandoverDate] = useState(job.handover?.date || new Date().toISOString().slice(0, 10));
    const [reservations, setReservations] = useState(job.handover?.reservations || "");
    const [saving, setSaving] = useState(false);

    const existingHandover = job.handover && job.handover.signature_url;

    // Setup canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#0F172A";
        ctx.lineWidth = 2.5;
    }, [existingHandover]);

    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        if (e.touches && e.touches[0]) {
            return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
        }
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        setIsDrawing(true);
        const ctx = canvasRef.current.getContext("2d");
        const { x, y } = getPos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext("2d");
        const { x, y } = getPos(e);
        ctx.lineTo(x, y);
        ctx.stroke();
        setHasSignature(true);
    };

    const stop = () => setIsDrawing(false);

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    };

    const submitHandover = async () => {
        if (!hasSignature && !existingHandover) {
            toast.error("Klient musí podepsat");
            return;
        }
        setSaving(true);
        try {
            const canvas = canvasRef.current;
            const dataUrl = hasSignature ? canvas.toDataURL("image/png") : null;
            const payload = { date: handoverDate, reservations };
            if (dataUrl) payload.signature_data_url = dataUrl;
            await api.post(`/jobs/${job.id}/handover`, payload);
            toast.success("Předávací protokol uložen");
            onUpdate();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Uložení selhalo");
        } finally {
            setSaving(false);
        }
    };

    const revoke = async () => {
        if (!window.confirm("Opravdu zrušit uložený podpis a předání?")) return;
        try {
            await api.delete(`/jobs/${job.id}/handover`);
            toast.success("Předání zrušeno");
            onUpdate();
        } catch { toast.error("Zrušení selhalo"); }
    };

    const protocolUrl = `${API_BASE}/jobs/${job.id}/protocol-pdf`;
    const warrantyUrl = `${API_BASE}/jobs/${job.id}/warranty-pdf`;

    return (
        <div className="space-y-6" data-testid="handover-panel">
            {existingHandover && (
                <div className="bg-emerald-50 border border-emerald-300 px-6 py-4 flex items-center justify-between" data-testid="handover-completed">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-1">Dílo předáno</div>
                        <div className="font-display text-lg font-black text-slate-900">Podepsáno {fmtDate(job.handover.date)}</div>
                        <div className="text-xs text-slate-600 mt-0.5">Záruka běží od tohoto data.</div>
                    </div>
                    <button onClick={revoke} data-testid="revoke-handover"
                            className="text-xs uppercase tracking-widest font-semibold text-slate-500 hover:text-red-700">
                        Zrušit předání
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200">
                    <div className="px-6 py-3 border-b-2 border-slate-900 font-display font-bold uppercase tracking-wider text-sm">Předání díla</div>
                    <div className="p-6 space-y-4">
                        <div>
                            <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Datum předání</label>
                            <input type="date" value={handoverDate} onChange={(e) => setHandoverDate(e.target.value)} data-testid="handover-date"
                                   className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Výhrady / Nedodělky</label>
                            <textarea rows={4} value={reservations} onChange={(e) => setReservations(e.target.value)} data-testid="handover-reservations"
                                      placeholder="Bez výhrad / seznam nedodělků..."
                                      className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600 resize-none" />
                        </div>
                    </div>
                </div>

                <div className="bg-white border border-slate-200">
                    <div className="px-6 py-3 border-b-2 border-slate-900 font-display font-bold uppercase tracking-wider text-sm flex items-center justify-between">
                        <span>Podpis klienta</span>
                        {existingHandover ? (
                            <span className="text-[10px] text-emerald-700 font-normal normal-case tracking-normal">✓ Uloženo</span>
                        ) : (
                            <button onClick={clear} data-testid="clear-signature" className="text-[10px] normal-case tracking-normal font-normal text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                                <RotateCcw className="w-3 h-3" /> Smazat
                            </button>
                        )}
                    </div>
                    <div className="p-4">
                        {existingHandover ? (
                            <img src={`${API_BASE}${job.handover.signature_url}`}
                                 alt="Podpis klienta" className="w-full h-40 object-contain border border-slate-200 bg-white" data-testid="existing-signature" />
                        ) : (
                            <canvas ref={canvasRef}
                                    onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
                                    onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}
                                    data-testid="signature-canvas"
                                    style={{ width: "100%", height: "160px", touchAction: "none" }}
                                    className="border border-slate-300 bg-slate-50 cursor-crosshair rounded-sm" />
                        )}
                        <p className="text-[10px] text-slate-500 mt-2 uppercase tracking-widest">
                            {existingHandover ? "Podepsáno" : "Podepište prstem/perem přímo na obrazovku"}
                        </p>
                    </div>
                </div>
            </div>

            {!existingHandover && (
                <div className="flex items-center justify-end">
                    <button onClick={submitHandover} disabled={saving} data-testid="submit-handover"
                            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50">
                        <Save className="w-4 h-4" /> {saving ? "Ukládám…" : "Podepsat a předat dílo"}
                    </button>
                </div>
            )}

            <div className="bg-slate-50 border border-slate-200 px-6 py-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-3">Dokumenty ke stažení</div>
                <div className="flex flex-wrap gap-3">
                    <a href={protocolUrl} target="_blank" rel="noreferrer" data-testid="download-protocol"
                       className="inline-flex items-center gap-2 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                        <Download className="w-3.5 h-3.5" /> Předávací protokol (PDF)
                    </a>
                    {job.warranty_months > 0 && (
                        <a href={warrantyUrl} target="_blank" rel="noreferrer" data-testid="download-warranty"
                           className="inline-flex items-center gap-2 border border-orange-600 text-orange-700 hover:bg-orange-600 hover:text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                            <Download className="w-3.5 h-3.5" /> Záruční list (PDF)
                        </a>
                    )}
                    {job.warranty_months === 0 && (
                        <span className="text-xs text-slate-500 italic self-center">Pro Záruční list nastavte v Informacích dobu záruky (měsíce).</span>
                    )}
                </div>
            </div>
        </div>
    );
}
